import React from "react";

import { DateRange } from "react-day-picker";

import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "~/components/ui/card";
import { Skeleton } from "~/components/ui/skeleton";
import { usePaymentMethodDistribution } from "~/pages/payments/hooks/usePaymentMethodDistribution";

interface PaymentMethodDistributionChartProps {
  dateRange?: DateRange;
  paymentMethod?: string;
}

// Map payment method values to Spanish labels
const PAYMENT_METHOD_LABELS: Record<string, string> = {
  transfer: "Transf. Manual",
  bank_transfer: "Transf. Fintoc",
  cash: "Efectivo",
  check: "Cheque",
  factoring: "Factoring",
  descuento_nomina: "Desc. nómina",
  rappel: "Rappel",
  webpay: "Webpay",
  khipu: "Khipu",
  fintoc: "Fintoc",
  getnet: "GetNet",
  manual: "Manual",
  other: "Otro",
};

// Colors for each payment method
const COLORS = [
  "#3b82f6", // blue
  "#10b981", // green
  "#f59e0b", // amber
  "#ef4444", // red
  "#8b5cf6", // violet
  "#ec4899", // pink
  "#06b6d4", // cyan
  "#84cc16", // lime
  "#f97316", // orange
  "#6366f1", // indigo
  "#14b8a6", // teal
  "#a855f7", // purple
  "#64748b", // slate
];

const PaymentMethodDistributionChart: React.FC<
  PaymentMethodDistributionChartProps
> = ({ dateRange, paymentMethod }) => {
  const { distribution, isLoading } = usePaymentMethodDistribution(dateRange);

  // Don't render chart when filtering by specific payment method
  if (paymentMethod) {
    return null;
  }

  // Transform data for the chart
  const chartData = distribution.map((item) => ({
    name: PAYMENT_METHOD_LABELS[item.method] || item.method,
    count: item.count,
    amount: item.total_amount,
  }));

  // Format currency for tooltip
  const formatCurrency = (value: number) => {
    return `$${value.toLocaleString("es-CL")}`;
  };

  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribución por Método de Pago</CardTitle>
          <CardDescription>
            Cantidad de pagos por método de pago
          </CardDescription>
        </CardHeader>
        <CardContent className="flex h-[300px] flex-col items-center justify-center gap-4">
          <Skeleton className="h-40 w-40 rounded-full" />
          <div className="flex flex-wrap justify-center gap-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-16" />
            <Skeleton className="h-4 w-24" />
            <Skeleton className="h-4 w-16" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!chartData || chartData.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Distribución por Método de Pago</CardTitle>
          <CardDescription>
            Cantidad de pagos por método de pago
          </CardDescription>
        </CardHeader>
        <CardContent className="flex h-[300px] items-center justify-center">
          <p className="text-muted-foreground">
            No hay datos disponibles para el período seleccionado
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8">
      <CardHeader>
        <CardTitle>Distribución por Método de Pago</CardTitle>
        <CardDescription>
          Cantidad de pagos por método de pago en el período seleccionado
        </CardDescription>
      </CardHeader>
      <CardContent>
        <ResponsiveContainer width="100%" height={350}>
          <PieChart>
            <Pie
              data={chartData}
              cx="50%"
              cy="50%"
              labelLine={false}
              label={({ name, percent }) => {
                // Only show label if percentage is >= 5%
                if (percent >= 0.05) {
                  return `${name}: ${(percent * 100).toFixed(0)}%`;
                }
                return "";
              }}
              outerRadius={100}
              fill="#8884d8"
              dataKey="count"
            >
              {chartData.map((entry, index) => (
                <Cell
                  key={`cell-${index}`}
                  fill={COLORS[index % COLORS.length]}
                />
              ))}
            </Pie>
            <Tooltip
              content={({ active, payload }) => {
                if (active && payload && payload.length) {
                  return (
                    <div className="rounded-lg border bg-white p-3 shadow-lg">
                      <p className="font-semibold">{payload[0].payload.name}</p>
                      <p className="text-sm text-muted-foreground">
                        Cantidad: {payload[0].value}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Total: {formatCurrency(payload[0].payload.amount)}
                      </p>
                    </div>
                  );
                }
                return null;
              }}
            />
            <Legend
              layout="horizontal"
              verticalAlign="bottom"
              align="center"
              wrapperStyle={{ paddingTop: "20px" }}
            />
          </PieChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

export default PaymentMethodDistributionChart;
